import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { getBorder } from 'Components/utils';

const styles = (theme) => ({
  root: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(1),
    ...getBorder(theme, {
      left: true,
      color: theme.palette.facet.lightGray2,
    }),
  },
  editingIcon: {
    fontSize: 14,
    marginRight: theme.spacing(0.5),
  },
  secondsRemaining: {
    fontSize: 14,
    margin: theme.spacing(0, 0.5),
    fontWeight: theme.typography.fontWeightBold,
  },
  warningTime: {
    color: theme.palette.facet.orange,
  },
  youreGood: {
    color: theme.palette.facet.primaryBlue,
  },
});

const EditMessageCountdownTimer = ({
  className,
  classes,
  messageResource,
  onComplete,
}) => {
  const editableUtilDatetime = moment(messageResource?.attr('editableUntil'));
  const [[mins, secs], setTimeLeft] = useState([null, null]);
  const timeExpired = (mins === 0 && secs === 0);

  const resetTimer = () => setTimeLeft([null, null]);
  const tick = () => {
    if (timeExpired) {
      resetTimer();
    } else if (secs === 0) {
      setTimeLeft([mins - 1, 59]);
    } else {
      setTimeLeft([mins, secs - 1]);
    }
  };

  useEffect(() => {
    let timerId;
    if (timeExpired) {
      setTimeout(() => onComplete(), 500); // <-- Shows Times Up!
      clearInterval(timerId);
    }
    timerId = setInterval(() => tick(), 1000);
    return () => clearInterval(timerId);
  }, [mins, secs]);

  // Calculates time left to make any changes
  const calculateTimeLeft = () => {
    const dateDiff = editableUtilDatetime.diff(moment());

    const minutes = Math.floor((dateDiff / 1000 / 60) % 60);
    const seconds = Math.floor((dateDiff / 1000) % 60);

    setTimeLeft([minutes, seconds]);
  };

  // Determine time left to edit
  useEffect(() => {
    calculateTimeLeft();
  }, [messageResource]);

  const EditMessage = () => (
    <>
      {'You have'}
      <span
        className={classNames(classes.secondsRemaining, {
          [classes.youreGood]: mins >= 1,
          [classes.warningTime]: mins === 0,
        })}
      >
        {`${mins}:${secs < 10 ? '0' : ''}${secs}`}
      </span>
      {'to make any changes.'}
    </>
  );

  const TimesUp = () => ('Times up! Reverting changes..');

  return (
    <Typography
      className={classNames(classes.root, className)}
      variant="h6"
      color="primaryGray"
    >
      {!timeExpired ? <EditMessage /> : <TimesUp />}
    </Typography>
  );
};

EditMessageCountdownTimer.defaultProps = {
  className: '',
};

EditMessageCountdownTimer.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  messageResource: PropTypes.object.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default withStyles(styles)(EditMessageCountdownTimer);
